// =============================================================================
// DriverAlerts - ride alerts for care team drivers (CHW, care manager)
// (Oct 2026, 20270105100000_ride_driver_alerts).
//
//   DriverLocationReporter  mounted once in the staff shell for driver roles.
//                           While the app is open, location sharing is on and
//                           the driver has a ride within 3 hours, it sends the
//                           phone's position every 2 minutes
//                           (staff_location_report). Nothing otherwise; the
//                           database also refuses a point outside that window.
//   DriverAlertsCard        on the Care Schedule and Today in the field:
//                           the next pickup and its leave-by time, Phone
//                           alerts on / off (web push through staff-push),
//                           and Share my location on / off, and Directions
//                           to the next pickup.
//
// Rides to accept (20270110100000_ride_driver_accept): rides someone else set
// up for this driver are listed with Accept / Can't make it (DriverRespond).
// A ride home handed to this driver after pickup is listed first
// (20270111100000_ride_home_safety).
//
// The leave-by and the alerts come from the ride-driver-alerts edge function
// (drive time with traffic from the driver's location, previous stop, or the
// office). Phone notifications never carry patient details.
// =============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { C } from "../../lib/tokens";
import { Btn } from "../ui";
import { useAuth } from "../../auth/AuthProvider";
import { fetchDriverStatus, setLocationSharing, reportLocation, fetchMyRideEvents } from "../../lib/ridesApi";
import { pickupTime, leaveByLine, rideErrorText, ALL_DRIVER_ROLES, rideWhen } from "../../lib/ridesCore";
import { pushState, enableStaffPush, disableStaffPush } from "../../lib/pwa";
import { toast } from "../ToastProvider";
import RideDirections from "./RideDirections";
import DriverRespond from "./DriverRespond";
import useRideLive from "../../hooks/useRideLive";
import { readShellKey } from "../../lib/shellCounts";

// Same list as the ride's driver roles (ALL_DRIVER_ROLES, _ride_is_driver_role).
export const DRIVER_ROLES_FOR_ALERTS = ALL_DRIVER_ROLES;
const REPORT_MS = 2 * 60000;
const STATUS_MS = 5 * 60000;

// Is a ride close enough to send the location? (pickup within 3 hours)
export function rideSoon(next, nowMs = Date.now()) {
  if (!next || !next.pickup_at) return false;
  const t = new Date(next.pickup_at).getTime();
  return t - nowMs <= 3 * 3600000 && t - nowMs >= -30 * 60000;
}

export function DriverLocationReporter() {
  const { role } = useAuth();
  const on = DRIVER_ROLES_FOR_ALERTS.includes(role);
  const status = useRef(null);
  useEffect(() => {
    if (!on || typeof navigator === "undefined" || !navigator.geolocation) return undefined;
    let live = true;
    // Round 7 #2: the timer reads the status from the shell's one read
    // (shell_counts, refreshed every 60s), so it costs no request of its own;
    // a sharing change asks the server at once.
    const read = (fresh) => (fresh ? fetchDriverStatus() : readShellKey("ride_status", fetchDriverStatus));
    const refresh = (fresh = false) => read(fresh).then((s) => { if (live) status.current = s; })
      // supabase-best-effort: no status means no location is sent this round.
      .catch(() => { status.current = null; });
    const send = () => {
      const s = status.current;
      if (!s || !s.sharing || !rideSoon(s.next) || document.visibilityState === "hidden") return;
      navigator.geolocation.getCurrentPosition(
        (p) => { reportLocation(p.coords.latitude, p.coords.longitude, p.coords.accuracy).catch(() => {}); },
        () => {},
        { enableHighAccuracy: false, maximumAge: 60000, timeout: 20000 },
      );
    };
    refresh().then(send);
    const a = setInterval(() => refresh(), STATUS_MS);
    const b = setInterval(send, REPORT_MS);
    const onShare = () => refresh(true).then(send);
    window.addEventListener("pos-driver-sharing", onShare);
    return () => { live = false; clearInterval(a); clearInterval(b); window.removeEventListener("pos-driver-sharing", onShare); };
  }, [on]);
  return null;
}

const PUSH_TEXT = {
  "needs-install": "On iPhone, add PracticeOS to your Home Screen (Share, then Add to Home Screen) and open it from there to turn on alerts.",
  blocked: "Notifications are blocked for PracticeOS on this phone. Allow them in the browser or phone settings.",
  unsupported: "This browser cannot show notifications.",
};

// compactHelp (round 6, My Engagements): the explainer as one line with
// "How this works"; other callers keep the full text.
export default function DriverAlertsCard({ compactHelp = false } = {}) {
  const { role, user, practiceId } = useAuth();
  const show = DRIVER_ROLES_FOR_ALERTS.includes(role);
  const [s, setS] = useState(null);
  const [push, setPush] = useState("off");
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);

  // Recent ride updates show here too: CHWs have no Inbox (Oct 2026).
  const load = useCallback(() => {
    fetchMyRideEvents(5).then(setEvents)
      // supabase-best-effort: the list is a convenience; alerts also go to the phone.
      .catch(() => setEvents([]));
    return fetchDriverStatus().then(setS)
      // supabase-best-effort: the card hides without status.
      .catch(() => setS(null));
  }, []);
  useEffect(() => {
    if (!show) return undefined;
    load();
    pushState().then(setPush).catch(() => setPush("unsupported"));
    const t = setInterval(load, STATUS_MS);
    return () => clearInterval(t);
  }, [show, load]);
  // Live: a new alert for this driver, or a change to the practice's rides
  // (accepted, handed on, moved), refreshes the card.
  useRideLive(load, { practiceId, driverId: user && user.id, tables: ["appointment_rides", "ride_driver_events"], intervalMs: 0, enabled: show });

  if (!show || !s) return null;
  const next = s.next;

  const togglePush = async () => {
    setBusy(true);
    try {
      const st = push === "on" ? await disableStaffPush() : await enableStaffPush();
      setPush(st);
      toast({ kind: st === "on" ? "success" : "info", text: st === "on" ? "Ride alerts are on for this phone." : st === "off" ? "Ride alerts are off for this phone." : PUSH_TEXT[st] || "Could not turn on alerts." });
    } catch (e) { toast({ kind: "error", text: "Could not change phone alerts: " + (e.message || e) }); }
    setBusy(false);
  };
  const toggleShare = async () => {
    setBusy(true);
    try {
      await setLocationSharing(!s.sharing);
      setS((x) => ({ ...x, sharing: !x.sharing }));
      if (!s.sharing) window.dispatchEvent(new Event("pos-driver-sharing"));
      toast({ kind: "success", text: !s.sharing ? "Location sharing is on for ride times." : "Location sharing is off. Your last location was cleared." });
    } catch (e) { toast({ kind: "error", text: rideErrorText(e) }); }
    setBusy(false);
  };

  return (
    <div data-testid="driver-alerts" style={{ padding: "10px 12px", borderRadius: 10, border: "0.5px solid var(--brand-border)", background: "var(--brand-bg)", marginBottom: 12 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>Ride alerts</span>
        {next ? (
          <span data-testid="driver-next" style={{ fontSize: 12, color: C.textSecondary }}>
            {"Next pickup " + pickupTime(next.pickup_at)}{leaveByLine(next) ? " - " + leaveByLine(next) : ""}
          </span>
        ) : <span style={{ fontSize: 12, color: C.textTertiary }}>No rides to drive coming up.</span>}
      </div>
      {next && next.ride_id && <RideDirections rideId={next.ride_id} />}
      {(s.to_accept || []).length > 0 && (
        <div data-testid="driver-to-accept" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, border: "1px solid " + C.warnFg, background: C.warnBg }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.warnFg }}>{"Rides to accept (" + s.to_accept.length + ")"}</div>
          {s.to_accept.map((a) => (
            <div key={a.ride_id} data-testid="driver-to-accept-row" style={{ marginTop: 6, paddingTop: 6, borderTop: "0.5px solid " + C.borderLight }}>
              <div style={{ fontSize: 12, color: C.textPrimary }}>{a.home
                ? "Ride home from the clinic - " + (a.appt_type || "visit") + " at " + pickupTime(a.start_at) + (["Completed", "Cancelled", "No Show"].includes(a.visit_status) ? " (now)" : "")
                : "Pickup " + rideWhen(a.pickup_at) + " - " + (a.appt_type || "visit") + " at " + pickupTime(a.start_at)}</div>
              <DriverRespond rideId={a.ride_id} driverId={user ? user.id : null} pending home={!!a.home}
                onDone={(action, text) => { toast({ kind: "success", text }); load(); }} />
            </div>
          ))}
        </div>
      )}
      {events.length > 0 && (
        <ul data-testid="driver-events" aria-label="Recent ride updates" style={{ listStyle: "none", margin: "8px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
          {events.map((e) => (
            <li key={e.id} style={{ fontSize: 12, color: e.event === "patient_waiting" || e.event === "leave_now" || e.event === "pickup_home" ? C.dangerFg : C.textSecondary }}>
              <span style={{ color: C.textTertiary }}>{pickupTime(e.created_at) + " - "}</span>{e.push_text}
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        <Btn size="sm" variant={push === "on" ? "success" : "outline"} disabled={busy || push === "unsupported"} onClick={togglePush}>
          {push === "on" ? "Phone alerts on" : "Turn on phone alerts"}
        </Btn>
        <Btn size="sm" variant={s.sharing ? "success" : "outline"} disabled={busy} onClick={toggleShare}>
          {s.sharing ? "Sharing location" : "Share my location"}
        </Btn>
      </div>
      {compactHelp ? (
        <CompactHelp short={PUSH_SHORT[push]} long={PUSH_TEXT[push]} />
      ) : (
        <>
          {PUSH_TEXT[push] && <div style={{ fontSize: 11, color: C.textSecondary, marginTop: 6 }}>{PUSH_TEXT[push]}</div>}
          <div style={{ fontSize: 11, color: C.textTertiary, marginTop: 6 }}>{HELP_TEXT}</div>
        </>
      )}
    </div>
  );
}

const HELP_TEXT = "You get a heads-up when a ride is set up, moved or cancelled, the afternoon before, and when it is time to leave."
  + " Your location is used only while a ride is within 3 hours and PracticeOS is open, and it is cleared after the day.";
const PUSH_SHORT = {
  "needs-install": "Add PracticeOS to your Home Screen to turn on alerts.",
  blocked: "Notifications are blocked on this phone.",
  unsupported: "This browser cannot show notifications.",
};

// Round 6 (My Engagements): the two long help lines sat above the CHW's
// visit list. One line here; the full text opens on "How this works".
function CompactHelp({ short, long }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="driver-alerts-help" style={{ fontSize: 12, color: C.textSecondary, marginTop: 6 }}>
      {/* One line even on a phone: the status text gives way (the full
          sentence is inside "How this works"), the link never wraps. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{short || "Alerts for rides you drive."}</span>
        <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} style={{ padding: 0, border: 0, background: "transparent",
          color: C.brandText, fontWeight: 600, fontSize: 12, fontFamily: "inherit", cursor: "pointer", textDecoration: "underline", minHeight: 24,
          flexShrink: 0, whiteSpace: "nowrap" }}>
          How this works
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 6, color: C.textTertiary, lineHeight: 1.5 }}>
          {long ? <div style={{ color: C.textSecondary, marginBottom: 4 }}>{long}</div> : null}
          {HELP_TEXT}
        </div>
      )}
    </div>
  );
}
